import { FULL_DAYS, WEEK_DAYS, type FullDay } from "../data/days";
import { schedule } from "../data/schedule";
import { T } from "../styles/theme";
import type { SpecialTab } from "../types";

interface DayTabsProps {
  activeDay: FullDay;
  specialTab: SpecialTab;
  checked: Record<string, boolean>;
  onSwitchDay: (day: FullDay) => void;
  onSpecialTab: (tab: SpecialTab) => void;
}

/** Horizontal scrollable row of weekday tabs followed by special-tab buttons. */
export function DayTabs({ activeDay, specialTab, checked, onSwitchDay, onSpecialTab }: DayTabsProps) {
  return (
    <div
      style={{
        background: "linear-gradient(135deg,#b8895a,#d4aa7d)",
        display: "flex",
        overflowX: "auto",
        scrollbarWidth: "none",
        padding: "0 6px",
        gap: 1,
        boxShadow: "0 2px 8px rgba(45,32,22,0.2)",
      }}
    >
      {FULL_DAYS.map((day, i) => {
        const isActive = day === activeDay && !specialTab;
        const dayTasks = schedule[day].tasks;
        const doneCount = dayTasks.filter((t) => t.id && checked[t.id]).length;
        const complete = doneCount === dayTasks.length;
        return (
          <button
            key={day}
            onClick={() => onSwitchDay(day)}
            style={{
              flex: "0 0 auto",
              padding: "10px 13px",
              background: isActive ? T.cream : "transparent",
              border: "none",
              cursor: "pointer",
              borderRadius: "8px 8px 0 0",
              transition: "all 0.2s",
              position: "relative",
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: isActive ? T.terra : "rgba(255,255,255,0.75)",
              }}
            >
              {WEEK_DAYS[i]}
            </div>
            <div style={{ fontSize: 8, marginTop: 2, color: "#6b3a10" }}>
              {doneCount}/{dayTasks.length}
            </div>
            {complete && (
              <div
                style={{
                  position: "absolute",
                  top: 5,
                  right: 5,
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: T.sageLight,
                }}
              />
            )}
          </button>
        );
      })}

      <SpecialTabButton
        active={specialTab === "monthly"}
        onClick={() => onSpecialTab("monthly")}
        icon="📅"
        label="Mthly"
        sub="anchors"
        activeBg={T.goldPale}
        activeColor="#c8834a"
      />
      <SpecialTabButton
        active={specialTab === "seasonal"}
        onClick={() => onSpecialTab("seasonal")}
        icon="🔄"
        label="Season"
        sub="upkeep"
        activeBg="#f0f0ff"
        activeColor="#9b6dd4"
      />
      <SpecialTabButton
        active={specialTab === "sickday"}
        onClick={() => onSpecialTab("sickday")}
        icon="🤒"
        label="Sick"
        sub="day plan"
        activeBg="#fff0ee"
        activeColor={T.blush}
      />
    </div>
  );
}

interface SpecialTabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
  sub: string;
  activeBg: string;
  activeColor: string;
}

function SpecialTabButton({ active, onClick, icon, label, sub, activeBg, activeColor }: SpecialTabButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: "0 0 auto",
        padding: "10px 11px",
        background: active ? activeBg : "transparent",
        border: "none",
        cursor: "pointer",
        borderRadius: "8px 8px 0 0",
        transition: "all 0.2s",
      }}
    >
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          color: active ? activeColor : "#3d1e0a",
        }}
      >
        {icon} {label}
      </div>
      <div style={{ fontSize: 8, marginTop: 2, color: active ? activeColor : "#6b3a10" }}>{sub}</div>
    </button>
  );
}
